import cn from 'classnames';
import { QueryParams } from 'common/urls/QueryParams';
import { useState } from 'react';
import landingPoster from 'src/assets/img/landing/Landing_Poster.jpg';
import landingPosterMobile from 'src/assets/img/landing/Landing_Poster_Mobile.jpg';
import curlyArrow from 'src/assets/svg/landing/curly_arrow.svg';
import landingVideo from 'src/assets/videos/homepage/landingVideoHero.mp4';
import landingVideoMobile from 'src/assets/videos/homepage/landingVideoHeroMobile.mp4';
import { useOnMount } from 'src/shared/hooks/useOnMount';
import { match } from 'ts-pattern';
import { StringParam, useQueryParam } from 'use-query-params';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';
import { useShowConsumerHeaderPersistentBanner } from '../../components/header-v2/useShowConsumerHeaderPersistentBanner';
import { LayoutContainer } from './LayoutContainer';

type HomepageHeroSectionProps = {
  exploreButtonOnClick: () => void;
  mealPlanButtonOnClick: () => void;
};

const WEEKLY_SHEF_SERVICE_FOCUSED_HOMEPAGES = ['04', '05', '06a', '06b', '07'];

export const HomepageHeroSection: React.FC<HomepageHeroSectionProps> = ({
  exploreButtonOnClick,
  mealPlanButtonOnClick,
}) => {
  const { isSmallScreen } = useScreenContext();
  const [loaded, setLoaded] = useState<boolean>(false);
  const [posterImg] = useState<string>(isSmallScreen ? landingPosterMobile : landingPoster);
  const [videoSrc] = useState<string>(isSmallScreen ? landingVideoMobile : landingVideo);
  const { enabled: promoBannerIsShowing } = useShowConsumerHeaderPersistentBanner();
  const [variantName] = useQueryParam(QueryParams.LANDING_PAGE_VARIANT, StringParam);
  const hideOneTimeCTA = WEEKLY_SHEF_SERVICE_FOCUSED_HOMEPAGES.includes(variantName ?? '');
  const [mainTitle, subtitle, subtitle2, bannerTitle, ctaText] = match(variantName)
    .with('01', () => [
      'HOMEMADE MEALS',
      'BY',
      'LOCAL SHEFS',
      'Hundreds of shefs, ready to cook weekly meals for your family.',
      'Browse shefs',
    ])
    .with('02', () => [
      'DINNER IS SERVED',
      'BY YOUR',
      'LOCAL SHEFS',
      'Fresh meals from local shefs, delivered weekly - heat & enjoy!',
      'Explore personal shefs',
    ])
    .with('03', () => [
      'MADE WITH CARE',
      'NEVER',
      'MANUFACTURED',
      'Fresh, high quality meals made by local shefs, delivered to your door.',
      "Browse this week's meals",
    ])
    .with('04', () => [
      'PERSONAL CHEFS',
      'MADE',
      'AFFORDABLE',
      'Dinnertime without the stress - starting at $72/week.',
      'Explore Weekly Shef Service',
    ])
    .with('05', () => [
      'A SHARED PERSONAL CHEF SERVICE',
      '',
      '',
      'Get a weekly delivery from local chefs from $72/week',
      'Explore Weekly Shef Service',
    ])
    .with('06a', () => [
      'NEW YORK CITY',
      'Share',
      'a personal chef',
      'Let a top local chef cook for you - from $72/week',
      'Explore Weekly Shef Service',
    ])
    .with('06b', () => [
      'BAY AREA',
      'Share',
      'a personal chef',
      'Let a top local chef cook for you - from $72/week',
      'Explore Weekly Shef Service',
    ])
    .with('07', () => [
      'THE BEST SHORTCUT',
      'to homemade dinners',
      '',
      'Share a personal chef and get quality meals from $72/week',
      'Explore Weekly Shef Service',
    ])
    .otherwise(() => [
      'HOME COOKING',
      'WITHOUT THE COOKING',
      '',
      'Weekly deliveries from your local, personal chef - from $54/week',
      'Explore Weekly Shef Service',
    ]);

  useOnMount(() => {
    setLoaded(true);
  });

  const backgroundHeight = {
    'h-[65svh] md:h-[85%]': promoBannerIsShowing,
    'h-[70svh] md:h-[90%]': !promoBannerIsShowing,
  };

  const sharedBackgroundStyles = 'absolute top-0 left-0 w-full';

  return (
    <section className='relative max-md:mb-8 max-sm:h-[calc(100svh-50px)] md:h-[820px] lg:h-[calc(100vw/(16/9))]'>
      <LayoutContainer className='relative h-full'>
        <div
          className={cn('flex flex-col items-center justify-end md:h-full md:justify-center md:gap-4', {
            'h-[85svh]': promoBannerIsShowing,
            'h-[90svh]': !promoBannerIsShowing,
          })}>
          <div className='flex flex-col items-center justify-center text-center text-white max-md:h-[40vh] md:h-auto'>
            <h1 className='text-6xl font-black md:text-8xl'>{mainTitle}</h1>
            <h2 className='-mt-6 font-juniper-bay text-8xl max-md:text-6xl max-md:leading-none md:-mt-10'>
              <span className='block max-md:mt-4 md:inline'>{subtitle}</span>
              <span className='block md:inline'>&nbsp;{subtitle2}</span>
            </h2>
          </div>
          <div className='flex w-full flex-col items-center justify-center'>
            <div className='w-[23.125rem] max-w-[98%] rounded-2xl bg-eggplant p-5 text-white max-sm:w-auto md:p-6'>
              <p className='mb-4 text-center text-xl'>{bannerTitle}</p>
              <Button
                buttonType={ButtonType.Primary}
                onClick={mealPlanButtonOnClick}
                iconName='mixingBowl'
                align='center'
                iconPosition='right'>
                {ctaText}
              </Button>
              {!hideOneTimeCTA && (
                <div className='mt-4 text-center'>
                  <Button
                    darkBackground
                    iconName='arrowRight'
                    iconPosition='right'
                    buttonType={ButtonType.TextLink}
                    onClick={exploreButtonOnClick}>
                    Or schedule a one-time order
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </LayoutContainer>

      <div
        className={cn(
          'z-background-overlay w-full bg-black opacity-50 md:max-h-[90vh]',
          sharedBackgroundStyles,
          backgroundHeight
        )}>
        <img src={curlyArrow} alt='' className='absolute bottom-[-60px] right-[50%] max-md:hidden' />
      </div>
      {loaded && (
        <video
          muted
          loop
          playsInline
          autoPlay
          controls={false}
          poster={posterImg}
          className={cn(
            'z-background object-cover object-center md:max-h-[90vh]',
            sharedBackgroundStyles,
            backgroundHeight
          )}>
          <source src={videoSrc} type='video/mp4' />
        </video>
      )}
    </section>
  );
};
